import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion, useInView } from 'framer-motion';
import { MessageSquare } from 'lucide-react';
import { Reveal, useTypewriter, EASE } from './helpers';

const CHAT_EXAMPLES = [
    {
        q: 'How should I handle the Acme renewal pushback?',
        a: "Sarah usually checked the usage trend over the last 60 days first [KU-12]. Her view was that pushback at renewal is normally a procurement signal — the usage data tells you whether it's that, or a real value problem [KU-17]. She avoided discounting straight away because it set the bar lower for next year [KU-19].",
        cites: ['KU-12', 'KU-17', 'KU-19'],
    },
    {
        q: 'When should I escalate to the exec sponsor?',
        a: "Sarah's rule was to escalate only after two failed champion conversations [KU-23]. Earlier escalation, she felt, burned executive attention you'd need at the actual renewal moment [KU-27]. She explicitly rejected the 'escalate early to be safe' approach [KU-28].",
        cites: ['KU-23', 'KU-27', 'KU-28'],
    },
    {
        q: 'Which accounts actually need a quarterly review?',
        a: "She looked at usage change over the quarter plus whether the original champion was still in seat [KU-31]. If both were green, a 20-minute check-in was enough — no formal review needed [KU-33]. She called calendar-driven reviews 'theatre' and skipped them [KU-34].",
        cites: ['KU-31', 'KU-33', 'KU-34'],
    },
];

export const PredecessorChat = () => {
    const reduce = useReducedMotion();
    const ref = useRef<HTMLDivElement>(null);
    const inView = useInView(ref, { once: false, margin: '-20%' });
    const [idx, setIdx] = useState(0);
    const [phase, setPhase] = useState<'q' | 'a'>('q');
    const current = CHAT_EXAMPLES[idx];

    useEffect(() => {
        if (!inView) return;
        setPhase('q');
        const tQ = setTimeout(() => setPhase('a'), Math.max(600, current.q.length * 22 + 200));
        return () => clearTimeout(tQ);
    }, [idx, inView, current.q.length]);

    useEffect(() => {
        if (!inView || phase !== 'a') return;
        const aTime = current.a.length * 14 + 2200;
        const t = setTimeout(() => setIdx((i) => (i + 1) % CHAT_EXAMPLES.length), aTime);
        return () => clearTimeout(t);
    }, [phase, inView, current.a.length]);

    const qText = useTypewriter(current.q, 22, inView && phase === 'q');
    const aText = useTypewriter(current.a, 14, inView && phase === 'a');

    const showCites = phase === 'a' && !reduce && aText.length === current.a.length;

    return (
        <section id="ask-the-role" ref={ref} className="py-32 scroll-mt-16 border-t border-border/40 bg-muted/20">
            <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <Reveal className="lg:col-span-5">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold mb-5">For the next person in the seat</p>
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.05] mb-6">
                        Ask the role.
                        <span className="text-muted-foreground/60"> Not the wiki.</span>
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                        Once we've captured enough, your new hire can ask questions in plain English and get answers straight from the previous person's own notes.
                    </p>
                    <p className="text-base text-muted-foreground leading-relaxed mb-6">
                        Every answer is cited back to a specific decision note. If the answer isn't in the captured material, the assistant says so — it never guesses.
                    </p>

                    <div className="flex flex-wrap gap-2">
                        {CHAT_EXAMPLES.map((ex, i) => (
                            <button
                                key={i}
                                onClick={() => setIdx(i)}
                                aria-pressed={i === idx}
                                aria-label={`Try example: ${ex.q}`}
                                className={`text-[11px] px-3 py-1.5 rounded-full border transition-all ${
                                    i === idx ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border/60 text-muted-foreground hover:bg-muted/40 hover:text-foreground'
                                }`}
                            >
                                {ex.q.split(' ').slice(0, 4).join(' ')}…
                            </button>
                        ))}
                    </div>
                </Reveal>

                <Reveal className="lg:col-span-7" delay={0.1}>
                    <div className="rounded-2xl border border-border/60 bg-card p-7 shadow-xl shadow-primary/[0.03]">
                        <div className="flex items-center gap-2 mb-5 pb-4 border-b border-border/50">
                            <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center">
                                <MessageSquare className="h-3.5 w-3.5 text-primary" />
                            </div>
                            <div>
                                <div className="text-xs font-semibold">Ask the handover</div>
                                <div className="text-[11px] text-muted-foreground">Previous role-holder: Sarah Chen, Head of Customer Success</div>
                            </div>
                        </div>

                        <div className="min-h-[260px]">
                            <div className="flex justify-end mb-5">
                                <div className="max-w-[85%] rounded-2xl rounded-tr-md bg-primary text-primary-foreground px-4 py-2.5 text-sm">
                                    {qText || <span className="opacity-40">…</span>}
                                    {phase === 'q' && qText.length < current.q.length && <span className="inline-block w-1 h-3.5 bg-primary-foreground/70 ml-0.5 align-middle animate-pulse" />}
                                </div>
                            </div>

                            <AnimatePresence>
                                {phase === 'a' && (
                                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="flex justify-start">
                                        <div className="max-w-[88%] rounded-2xl rounded-tl-md bg-muted/60 px-4 py-3 text-sm leading-relaxed text-foreground/85">
                                            {aText || <span className="text-muted-foreground/50">Looking through Sarah's notes…</span>}
                                            {aText.length > 0 && aText.length < current.a.length && <span className="inline-block w-1 h-3.5 bg-foreground/40 ml-0.5 align-middle animate-pulse" />}
                                            {showCites && (
                                                <motion.div
                                                    initial="hidden"
                                                    animate="show"
                                                    variants={{
                                                        hidden: {},
                                                        show: { transition: { staggerChildren: 0.12 } },
                                                    }}
                                                    className="flex flex-wrap items-center gap-1.5 mt-3 pt-3 border-t border-border/40"
                                                >
                                                    <span className="text-[10px] text-muted-foreground mr-1">From:</span>
                                                    {current.cites.map((c) => (
                                                        <motion.span
                                                            key={c}
                                                            variants={{
                                                                hidden: { opacity: 0, scale: 0.85, y: 4 },
                                                                show: { opacity: 1, scale: 1, y: 0 },
                                                            }}
                                                            transition={{ duration: 0.3, ease: EASE }}
                                                            className="text-[10px] font-mono px-2 py-0.5 rounded-md border border-primary/30 bg-primary/5 text-primary"
                                                        >
                                                            {c}
                                                        </motion.span>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className="pt-4 mt-3 border-t border-border/40 flex items-center justify-between text-[11px] text-muted-foreground">
                            <span>Answers from 22 captured notes · refuses anything not in the notes</span>
                            <span className="font-mono">
                                {idx + 1} / {CHAT_EXAMPLES.length}
                            </span>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
